import { createFileRoute } from "@tanstack/react-router";
import {
  FinanceDebtStatusOptions,
  SuppliersDebtsTableColumns,
} from "@/components/finance-debt/finance-debt-meta";
import { FinanceDebtModals } from "@/components/finance-debt/finance-debt-modals";
import { Button } from "@/components/ui/button";
import FullPagination from "@/components/ui/shared/full-pagination";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import TableSettings from "@/components/ui/shared/table-settings";
import SimpleTable from "@/components/ui/table";
import { Toast } from "@/components/ui/toast";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import { useTranslate } from "@/hooks/use-translate";
import useAppStore from "@/store";
import { Banknote, Plus } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute(
  "/(dasboard)/finance/debts/suppliers-debts"
)({
  component: SuppliersDebts,
});

function SuppliersDebts() {
  const store = useAppStore();
  const { t, translateOptions } = useTranslate();

  const tableAttributes = useTableAttributes();
  const tableName = "supplier_debts";

  const searchForm = useSimpleForm({
    keyword: undefined,
    status: undefined,
  });

  const search = ({ page = 1, per_page = 10 } = {}) => {
    store.financeDebt.fetch({
      page,
      per_page,
      ...searchForm.values,
      payer_type: "Supplier",
      receiver_type: "Shop",
    });
  };

  const handleAutocomplete = (values: any) => {
    store.financeDebt.paginate(
      store.financeDebt.items.filter((fd: any) => {
        const amountMatch = values.keyword
          ? fd.amount.toString().includes(values.keyword)
          : true;

        const status = values.status ? fd.status == values.status : true;
        return amountMatch && status;
      })
    );
  };

  useEffect(() => {
    if (store.auth.session) search();

    tableAttributes.loadAttributes(tableName, [
      "amount",
      "remaining",
      "supplier",
      "due_date",
      "payment_date",
      "status",
      "created_at",
      "actions",
    ]);
  }, [store.auth.session]);

  const columns = SuppliersDebtsTableColumns({
    store,
    onView: (debt: any) => {
      store.financeDebt.setCurrent(debt);
      store.display.show("view_finance_debt");
    },
    onEdit: (debt: any) => {
      store.financeDebt.setCurrent(debt);
      store.display.show("edit_finance_debt");
    },
  }).filter((item) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  return (
    <>
      <FinanceDebtModals />
      <Toast
        name="clipboard"
        message={t("Copy to clipboard")}
        autoClose={2000}
      />

      <div className="max-w-6xl mx-auto">
        <header className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4">
          <h2 className="text-2xl font-semibold"> {t("Suppliers debts")}</h2>
          <Button
            onClick={() => {
              store.display.show("create_supplier_debt");
            }}
          >
            <Plus className="w-4 h-4" />
            {t("Add supplier debt")}
          </Button>
        </header>
        <TableSettings
          columns={SuppliersDebtsTableColumns({ store })}
          tableName={tableName}
        >
          <SearchEngine
            autoComplete={handleAutocomplete}
            form={searchForm}
            onSubmit={undefined}
            selects={[
              {
                placeholder: t("All Statuses"),
                options: translateOptions(FinanceDebtStatusOptions),
                name: "status",
                icon: <Banknote className="w-4 h-4" />,
              },
            ]}
          />
        </TableSettings>

        <SimpleTable
          items={store.financeDebt.pagination.data}
          columns={columns}
        />

        <FullPagination
          className="mt-4"
          canEdit={true}
          pagination={store.financeDebt.pagination}
          onGoto={(page) => search({ page })}
          onPerPage={(per_page) => search({ per_page })}
        />
      </div>
    </>
  );
}
